import {
  Box,
  Button,
  Footer,
  GridedContent,
  GridedLayout,
  GridedMain,
  Header,
  Stepper,
} from '@capitalise/ui-design-system';
import useMediaQuery from 'modules/Business/hooks/useMediaQuery';
import { createElement, Fragment, FunctionComponent, memo, useRef, useState } from 'react';
import usePagination from 'hooks/usePagination';
import styled from 'styled-components';
import { use100vh } from 'react-div-100vh';
import { useModalContext } from 'hooks/useModalContext';
import { Analytics } from 'utils/Analytics';
import { AnalyticsEventAction, AnalyticsEventCategory } from 'enums/analytics-types';

export interface IStepsConfig {
  key?: string;
  content?: Function;
  icon?: string;
  label: string;
  lead?: string;
  title?: string;
  component?: (props: any) => JSX.Element;
  hasPrevious: boolean;
  image?: FunctionComponent;
  nextButtonLabel?: string;
  nextButtonAction?: Function;
  iconButton?: (props: any) => JSX.Element;
  children?: (props: any) => JSX.Element;
  asyncCustomNextFunction?: Function;
  skipButtonLabel?: string;
  isLastStep?: boolean;
  skipCallback?: Function;
  forceDisableNextButton?: boolean;
  previousCustomFunction?: Function;
  fullComponent?: {
    component: (props: any) => JSX.Element;
    wrapper?: (props: any) => JSX.Element;
    componentProps?: any;
  };
}

export interface IWizzardAlphaProps {
  stepsConfig: IStepsConfig[];
  progressStep?: Function;
  headerActions: (props: any) => JSX.Element;
  stepper?: {
    showOnlyStepper?: boolean;
    variant: 'default' | 'condensed';
  };
  heightDiff?: number;
  contextHook?: () => { metaData: object };
  customZIndex?: number;
}

const FullWidthGridedContent = styled(GridedContent)`
  width: 100%;
`;

const NoPaddingFooter = styled(Footer)`
  padding: 0;
`;

const WizzardAlpha = ({
  stepsConfig,
  headerActions,
  stepper,
  heightDiff = 0,
  customZIndex,
  contextHook = useModalContext,
}: IWizzardAlphaProps) => {
  const [loadingNext, setLoadingNext] = useState(false);
  const { metaData } = contextHook();
  const contentRef = useRef(null);

  const { isSm } = useMediaQuery();

  const { page: currentStep, goNext, goPrevious } = usePagination(0);

  const steps = stepsConfig.map(({ label }) => label);

  const stepConfig = stepsConfig[currentStep];

  const nextLabel = stepConfig?.nextButtonLabel || 'Next';

  const onSkipClick = () => {
    stepConfig?.skipCallback();

    if (stepsConfig[currentStep]?.isLastStep) {
      return;
    }

    goNext();
  };

  const onNextClick = async (options) => {
    if (stepConfig?.asyncCustomNextFunction) {
      setLoadingNext(true);

      try {
        await stepConfig?.asyncCustomNextFunction(metaData);
      } catch (error) {
        setLoadingNext(false);
        return;
      }
      setLoadingNext(false);
      goNext();
      return;
    }

    if (stepConfig?.nextButtonAction) {
      stepConfig?.nextButtonAction(options);
    }

    if (stepConfig?.isLastStep) {
      return;
    }
    Analytics.track(AnalyticsEventCategory.onboard, AnalyticsEventAction.answered, {
      extraParams: { key: stepsConfig[currentStep]?.key },
    } as any);

    goNext();
    contentRef?.current?.scrollTo(0, 0);
  };

  const Wrapper = stepsConfig[currentStep]?.fullComponent?.wrapper || Fragment;
  const height = use100vh();

  const disableNextButton =
    ((stepConfig?.skipButtonLabel || stepConfig?.forceDisableNextButton) &&
      metaData?.[stepConfig.label]?.disableNextButton) ??
    false;
  return (
    <Box
      backgroundColor="white"
      height={height - heightDiff}
      {...(customZIndex ? { zIndex: customZIndex } : {})}
    >
      <GridedLayout height={height - heightDiff}>
        {stepper?.showOnlyStepper ? (
          <Box
            gridArea="header"
            border="1px solid"
            borderColor="grey-100"
            paddingX="xsmall"
            paddingY={{ _: '3xsmall', md: '0' }}
            width="100%"
            height={{ _: 'auto', md: '3xlarge' }}
            backgroundColor="white"
            display="flex"
            alignItems="center"
          >
            <Stepper
              color="green"
              steps={steps}
              currentStep={+currentStep + 1}
              // @ts-ignore
              variant={stepper?.variant || 'default'}
            />
          </Box>
        ) : (
          <Header actions={createElement(headerActions)} isPoweredByExperian>
            <Box>
              <Stepper
                color="green"
                steps={steps}
                currentStep={+currentStep + 1}
                // @ts-ignore
                variant={stepper?.variant || 'default'}
              />
            </Box>
          </Header>
        )}

        {stepsConfig[currentStep]?.fullComponent ? (
          <Wrapper>
            {createElement(stepsConfig[currentStep].fullComponent.component, {
              onPreviousClick: goPrevious,
              onSubmitCallback: onNextClick,
              ...stepsConfig[currentStep],
              ...stepsConfig[currentStep].fullComponent.componentProps,
            })}
          </Wrapper>
        ) : (
          <>
            <GridedMain ref={contentRef}>
              {createElement(stepConfig?.component, {
                isSm,
                onPreviousClick: () => {
                  stepConfig?.previousCustomFunction && stepConfig.previousCustomFunction();
                  goPrevious();
                },
                ...stepConfig,
              })}
            </GridedMain>
            <NoPaddingFooter padding={0} zIndex={11}>
              <FullWidthGridedContent>
                <Box
                  width="100%"
                  display="flex"
                  gridGap="4xsmall"
                  gridColumn={{ _: '1/5', sm: '3/7', lg: '5/9' }}
                >
                  {stepConfig?.skipButtonLabel && (
                    <Button
                      baseColor="green"
                      fullWidth
                      variant="outlined"
                      onClick={onSkipClick}
                      aria-label={stepConfig?.skipButtonLabel}
                      data-testid="skipStepButton"
                    >
                      {stepConfig?.skipButtonLabel}
                    </Button>
                  )}
                  <Button
                    baseColor="green"
                    rightIcon={
                      stepConfig?.iconButton ? createElement(stepConfig?.iconButton) : null
                    }
                    fullWidth
                    onClick={onNextClick}
                    loading={loadingNext}
                    disabled={disableNextButton}
                    aria-label={nextLabel}
                    data-testid="nextStepButton"
                  >
                    {nextLabel}
                  </Button>
                </Box>
              </FullWidthGridedContent>
            </NoPaddingFooter>
          </>
        )}
      </GridedLayout>
    </Box>
  );
};

export default memo(WizzardAlpha, (prev, next) => prev.customZIndex !== next.customZIndex);
